import React, { useState, useEffect } from 'react';
import { X, Save, Edit, Plus, Trash2, AlertCircle } from 'lucide-react';

interface Bank {
  id: string;
  name: string;
  logo: string;
  offers: string[];
}

interface BankOfferEditorProps {
  isOpen: boolean;
  onClose: () => void;
  bank: Bank | null;
  onSave: (bankId: string, offers: string[]) => void;
}

const BankOfferEditor: React.FC<BankOfferEditorProps> = ({
  isOpen,
  onClose,
  bank,
  onSave
}) => {
  const [offers, setOffers] = useState<string[]>([]);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    if (bank) {
      setOffers(bank.offers || []);
      setErrors([]);
    }
  }, [bank]);

  if (!isOpen || !bank) return null;

  const handleOfferChange = (index: number, value: string) => {
    const newOffers = [...offers];
    newOffers[index] = value;
    setOffers(newOffers);
    
    // Clear error for this index if it exists
    if (errors[index]) {
      const newErrors = [...errors];
      newErrors[index] = '';
      setErrors(newErrors);
    }
  };

  const handleAddOffer = () => {
    setOffers([...offers, '']);
  };

  const handleRemoveOffer = (index: number) => {
    const newOffers = [...offers];
    newOffers.splice(index, 1);
    setOffers(newOffers);
    
    // Remove error for this index
    const newErrors = [...errors];
    newErrors.splice(index, 1);
    setErrors(newErrors);
  };

  const validateOffers = () => {
    const newErrors = offers.map(offer => 
      !offer.trim() ? 'Fırsat metni boş olamaz' : ''
    );
    
    setErrors(newErrors);
    return !newErrors.some(error => error);
  };

  const handleSave = () => {
    if (validateOffers()) {
      onSave(bank.id, offers);
      onClose();
    }
  };

  // Function to highlight monetary values with marker effect
  const highlightMonetaryValues = (text: string) => {
    if (!text) return text;
    
    // Regular expression to match monetary values like "25.000 TL" or "1.500TL"
    const regex = /(\d+(?:\.\d+)?)\s*(?:TL|₺|TLL)/g;
    
    // Split the text by monetary values and keep the delimiters
    const parts = [];
    let lastIndex = 0;
    let match;
    
    // Create a new regex object to use with exec
    const regexObj = new RegExp(regex);
    
    // Find all matches and their positions
    while ((match = regexObj.exec(text)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      
      // Add the match as a React element
      parts.push(
        <span key={`money-${match.index}`} className="bg-[#F7BA45]/30 px-1 py-0.5 rounded font-semibold">
          {match[0]}
        </span>
      );
      
      // Update lastIndex to continue after this match
      lastIndex = match.index + match[0].length;
    }
    
    // Add any remaining text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }
    
    return parts;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div 
          className="relative bg-white rounded-2xl w-full max-w-lg shadow-2xl"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img 
                  src={bank.logo} 
                  alt={bank.name}
                  className="h-8 object-contain"
                />
                <h2 className="text-xl font-bold text-slate-800">
                  {bank.name} Fırsatları
                </h2>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-slate-100 rounded-full transition-colors"
              >
                <X size={20} className="text-slate-500" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            <p className="text-slate-600">
              Banka için gösterilecek fırsat metinlerini düzenleyin. Parasal değerler otomatik olarak vurgulanacaktır.
            </p>

            <div className="space-y-4">
              {offers.map((offer, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-slate-700">
                      Fırsat {index + 1}
                    </label>
                    {offers.length > 1 && (
                      <button
                        onClick={() => handleRemoveOffer(index)}
                        className="p-1 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                  <input
                    type="text"
                    value={offer}
                    onChange={(e) => handleOfferChange(index, e.target.value)}
                    placeholder="Örn: %0 Faizli 60.000 TL'ye varan Nakit Fırsatı!"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45] transition-colors ${
                      errors[index] ? 'border-red-300 bg-red-50' : 'border-slate-200'
                    }`}
                  />
                  {errors[index] && (
                    <p className="text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle size={14} />
                      {errors[index]}
                    </p>
                  )}
                  <div className="mt-2">
                    <p className="text-xs text-slate-500">Önizleme:</p>
                    <div className="mt-1 p-2 bg-slate-50 rounded-lg">
                      {highlightMonetaryValues(offer)}
                    </div>
                  </div>
                </div>
              ))}

              {offers.length < 5 && (
                <button
                  onClick={handleAddOffer}
                  className="w-full flex items-center justify-center gap-2 p-2 border border-dashed border-slate-300 rounded-lg text-slate-600 hover:bg-slate-50 hover:border-slate-400 transition-colors"
                >
                  <Plus size={16} />
                  <span>Yeni Fırsat Ekle</span>
                </button>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-slate-200">
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-slate-600 hover:text-slate-900 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={handleSave}
                className="flex items-center gap-2 px-4 py-2 bg-[#F7BA45] text-slate-900 rounded-lg hover:bg-[#e5ac3d] transition-colors"
              >
                <Save size={20} />
                <span>Kaydet</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BankOfferEditor;